import { FormEvent, useEffect, useState } from "react";
import { AlertTriangle, Copy, KeyRound, Mail, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteChrome";
import { consultingOperationsImage } from "@/data/site";
import { trpc } from "@/lib/trpc";

/**
 * The three screens of client sign-in: ask for a link, set up the
 * authenticator, enter a code.
 *
 * They are separate routes rather than one component with a mode, because the
 * magic-link redemption in the Worker sends an unenrolled user to /secure and
 * an enrolled one to /verify. Landing someone on a code prompt for an
 * authenticator they have not set up is the single most confusing thing this
 * flow could do.
 */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <SiteLayout>
      <div className="cp-page cp-page--narrow cp-page--auth">
        {/* Purely decorative, and marked so: it carries no information a
            screen reader needs, and the sign-in card below holds every word
            on the page. Being decorative is also what keeps it safe - there
            is no text over the photograph, so no contrast to get wrong. */}
        <div className="cp-auth-hero" aria-hidden="true">
          <div className="cp-auth-hero__image" style={{ backgroundImage: `url(${consultingOperationsImage})` }} />
          <div className="cp-auth-hero__shade" />
        </div>
        <section className="section">
          <div className="site-container cp-auth">{children}</div>
        </section>
      </div>
    </SiteLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* 1. Request a link                                                          */
/* -------------------------------------------------------------------------- */

export function ClientLogin() {
  const [sent, setSent] = useState(false);
  const [expired, setExpired] = useState(false);

  // Read in an effect, never during render: wouter gives the pathname without
  // the query string, and touching window.location during render makes the
  // server pass and the first client render disagree.
  useEffect(() => {
    setExpired(new URLSearchParams(window.location.search).has("expired"));
  }, []);

  const requestLogin = trpc.clientPortal.requestLogin.useMutation({
    onSuccess: () => setSent(true),
    onError: error => toast.error("That did not go through", { description: error.message }),
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    requestLogin.mutate({ email: String(form.get("email") ?? "") });
  }

  if (sent) {
    return (
      <Shell>
        {/* Identical whether or not the address is on file. Confirming it
            would turn this form into a list of Altitude's clients. */}
        <div className="cp-auth__sent">
          <Mail size={24} />
          <h2>Check your email.</h2>
          <p>
            If that address has portal access, a sign-in link is on its way. It works once and
            expires in 20 minutes. You will be asked for your authenticator code after opening it.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="eyebrow">Altitude client portal</p>
      <h1>Sign in.</h1>
      {expired && (
        <div className="cp-auth__warning">
          That sign-in link has already been used or has expired. Request a new one below.
        </div>
      )}
      <p>No password. We email you a link, then ask for the code from your authenticator app.</p>
      <form onSubmit={onSubmit}>
        <label className="field">
          <span>Email address *</span>
          <input name="email" type="email" inputMode="email" autoComplete="email" required autoFocus />
        </label>
        <button className="button button--coral button--full" disabled={requestLogin.isPending}>
          {requestLogin.isPending ? "Sending…" : "Email me a sign-in link"}
        </button>
      </form>
      <a className="cp-auth__link" href="/clients/register">Not a client yet? Register an engagement</a>
    </Shell>
  );
}

/* -------------------------------------------------------------------------- */
/* 2. Enrol an authenticator                                                  */
/* -------------------------------------------------------------------------- */

export function ClientSecure() {
  const utils = trpc.useUtils();
  const [offer, setOffer] = useState<{ secret: string; uri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);

  const begin = trpc.clientPortal.beginEnrolment.useMutation({
    onSuccess: setOffer,
    onError: error => toast.error("Could not start setup", { description: error.message }),
  });
  const confirm = trpc.clientPortal.confirmEnrolment.useMutation({
    onSuccess: result => setRecoveryCodes(result.recoveryCodes),
    onError: error => toast.error("Code not accepted", { description: error.message }),
  });

  // Started once on mount. The secret is sealed server-side at this point, so
  // there is nothing sensitive held in the page between the two steps.
  useEffect(() => {
    begin.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onConfirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    confirm.mutate({ code: String(form.get("code") ?? "") });
  }

  if (recoveryCodes) {
    return (
      <Shell>
        <span className="cp-auth__tick"><ShieldCheck size={28} /></span>
        <p className="eyebrow">Two-step verification is on</p>
        <h1>Save these codes.</h1>
        <p>
          Each one signs you in once if you lose your phone. We store them hashed, so this is the
          only time they can be shown — not even Altitude can retrieve them later.
        </p>
        <ul className="cp-codes">
          {recoveryCodes.map(code => <li key={code}>{code}</li>)}
        </ul>
        <button
          type="button"
          className="button button--outline-dark"
          onClick={() => {
            void navigator.clipboard?.writeText(recoveryCodes.join("\n"));
            toast.success("Recovery codes copied.");
          }}
        >
          <Copy size={15} />Copy all
        </button>
        <label className="cp-confirm">
          <input type="checkbox" checked={saved} onChange={event => setSaved(event.target.checked)} />
          <span>I have saved these somewhere safe.</span>
        </label>
        <button
          className="button button--coral button--full"
          disabled={!saved}
          onClick={() => {
            void utils.clientPortal.session.invalidate();
            window.location.assign("/clients/portal");
          }}
        >
          Open my portal
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <p className="eyebrow">One more step</p>
      <h1>Secure your account.</h1>
      <p>
        Your portal holds executed agreements and billing details, so it needs a second factor.
        Scan this with Google Authenticator, 1Password, Authy or any authenticator app.
      </p>

      {begin.isPending && <p>Preparing your setup…</p>}

      {offer && (
        <>
          <div className="cp-secret">
            <Smartphone size={18} />
            <div>
              <small>Can't scan? Enter this key by hand:</small>
              <code>{offer.secret.replace(/(.{4})/g, "$1 ").trim()}</code>
            </div>
          </div>
          {/* The otpauth URI is offered as a link for mobile, where tapping it
              opens the authenticator directly. */}
          <a className="cp-auth__link" href={offer.uri}>Open in my authenticator app</a>

          <form onSubmit={onConfirm}>
            <label className="field">
              <span>Enter the 6-digit code to confirm *</span>
              <input
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
              />
            </label>
            <button className="button button--coral button--full" disabled={confirm.isPending}>
              {confirm.isPending ? "Checking…" : "Confirm and continue"}
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. Enter a code                                                            */
/* -------------------------------------------------------------------------- */

export function ClientVerify() {
  const utils = trpc.useUtils();
  const [useRecovery, setUseRecovery] = useState(false);

  const verify = trpc.clientPortal.verifySecondFactor.useMutation({
    onSuccess: () => {
      void utils.clientPortal.session.invalidate();
      window.location.assign("/clients/portal");
    },
    onError: error => toast.error("Not accepted", { description: error.message }),
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    verify.mutate({ code: String(form.get("code") ?? "") });
  }

  return (
    <Shell>
      <span className="cp-auth__tick"><KeyRound size={26} /></span>
      <p className="eyebrow">Two-step verification</p>
      <h1>Enter your code.</h1>
      <p>
        {useRecovery
          ? "Enter one of the recovery codes you saved when you set up two-step verification. Each one works once."
          : "Open your authenticator app and enter the current 6-digit code for Altitude."}
      </p>

      <form onSubmit={onSubmit}>
        <label className="field">
          <span>{useRecovery ? "Recovery code *" : "Authenticator code *"}</span>
          <input
            name="code"
            key={useRecovery ? "recovery" : "totp"}
            inputMode={useRecovery ? "text" : "numeric"}
            autoComplete="one-time-code"
            maxLength={useRecovery ? 24 : 6}
            required
            autoFocus
          />
        </label>
        <button className="button button--coral button--full" disabled={verify.isPending}>
          {verify.isPending ? "Checking…" : "Verify and continue"}
        </button>
      </form>

      <button type="button" className="cp-auth__link cp-auth__link--button" onClick={() => setUseRecovery(current => !current)}>
        {useRecovery ? "Use my authenticator app instead" : "Lost your phone? Use a recovery code"}
      </button>

      <div className="cp-auth__fineprint">
        <AlertTriangle size={13} /> Five incorrect codes and this link stops working. You can always
        request a new sign-in link.
      </div>
    </Shell>
  );
}
